import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import type { Product, ProductPlan } from '../types';

export interface ProductWithPlans {
  product: Product;
  plans: ProductPlan[]; // visible plans only: what buyers see today
  plansFailed?: boolean; // its prices didn't load, so `plans` says nothing
}

/** The company's products, each with its public (visible) plans. */
export function useProductCatalog(companyId: string) {
  const [catalog, setCatalog] = useState<ProductWithPlans[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const products = await api.listProducts(companyId);
      const entries = await Promise.all(
        products.map(async (product) => {
          try {
            const plans = await api.listProductPlans(companyId, product.id, 'visible');
            return { product, plans };
          } catch {
            return { product, plans: [], plansFailed: true };
          }
        })
      );
      setCatalog(entries);
      if (entries.some((e) => e.plansFailed)) {
        setError("Couldn't load all of your products' prices.");
      }
    } catch (err) {
      console.error('Failed to load products:', err);
      setError("Couldn't load your products. Try again.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { catalog, loading, error, reload };
}

/** A product members can join for free: every public plan costs nothing. */
export function isFreeProduct(entry: ProductWithPlans): boolean {
  return entry.plans.length > 0 && entry.plans.every((p) => p.price === 0);
}
